import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import Modal from 'react-bootstrap/Modal';
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from 'yup'

import { RootState, Dispatcher, LocationSelectorState, ShippingInformationState, ConstructionState, IPump, MeshSettingState, IMeshSetting } from "AppTypes";
import { locationSelectorActions } from "@features/location/selector/state_management/actions";
import { shippingInformationActions } from "@features/shippinginformation/state_management/actions";
import { constructionActions } from "@features/construction/state_management/actions";
import { overlappingActions } from "@features/setting/overlapping/state_management/actions";
import { success, error } from '@services/toast-service';
import Button from 'react-bootstrap/Button';
import { api } from "@services";
import { useQuery } from '@utils/func'
import { useDisplayMode } from '@utils/useDisplayMode'
import ThreeEditor from "./components/ThreeEditor";
import Overlay2D from "../../components/Overlay2D";
import ListMesh from "./components/ListMesh";
import LoadingSpinner from '../../components/LoadingSpinner'
import Mode2dEditor from "./components/Mode2dEditor";
import { STATUS_MESH } from '../../const/constants';
import { authActions } from '../auth/state_management/actions';
import ShippingInformationList from "./components/ShippingInformationList";
import './Construction.css'
import { meshSettingActions } from "@features/setting/meshcolor/state_management/actions";

type Props = {
    dispatch: Dispatcher;
    locationSelectorState: LocationSelectorState;
    shippingInformationState: ShippingInformationState;
    constructionState: ConstructionState;
    meshSettingState: MeshSettingState;
};

const meshSettingValidationSchema = Yup.object().shape({
    data: Yup.array().of(
        Yup.object().shape({
            transferTime: Yup.number()
                .integer("整数で入力してください")
                .min(0, '透過率は0～100の数値で入力してください')
                .max(100, '透過率は0～100の数値で入力してください')
                .required('透過率を入力してください'),
        })
    )
  })

interface IDataMeshSetting {
    data: IMeshSetting[],
}

const ConstructionPage: React.FC<Props> = ({
    dispatch,
    locationSelectorState,
    shippingInformationState,
    constructionState,
}) => {
    const [show, setShow] = useState(false);
    const [showModalDelete, setShowModalDelete] = useState(false)
    const [showModalStartStatus, setShowModalStartStatus] = useState(false)
    const [showModalSuccessStatus, setShowModalSuccessStatus] = useState(false)
    const [showMode, changeShowMode] = useState(1) // 1: 3d, 2: 2d
    const { is2D } = useDisplayMode() // #8432: system-wide 2D mode
    // SKY-171: otherPump = a 未打設 cell assigned to a ポンプ車 other than the selected one (gray)
    const [meshActive2D, setMeshActive2D] = useState<{ layer: number; row: number; meshId?: string; pumpId?: string; code?: string; otherPump?: boolean } | null>(null) // #8431: tapped 2D cell
    // #8424 Q-002: which per-cell modal is open, keyed by the tapped cell's CURRENT status
    // index (STATUS_MESH: 0=未打設→開始, 1=打設中→終了/開始取消, 2=打設終了→終了取消). null = closed.
    const [modal2DStatus, setModal2DStatus] = useState<number | null>(null)
    const query = useQuery();
    const locationSelectQuery = query.get('locationSelect')
    const pumpSelectQuery = query.get('pumpSelect')
    const mode3d = query.get('mode3d')
    const modeShowFloor = query.get('modeShowFloor')
    const floorActive = query.get('floorActive')
    const isShowMeshNotComplete = query.get('isShowMeshNotComplete');
    const [dataMeshSetting, setDataMeshSetting] = useState<IDataMeshSetting>({data: [],});
    const [isShowModal, setShowModal] = useState(false);
    const [isFirstLoadParam, setFirstLoadFarm] = useState(false);
    const getMeshFirst = useCallback(async () =>
    {
        if (locationSelectQuery && !isFirstLoadParam)
        {
            dispatch(locationSelectorActions.changeLocationSelect(locationSelectQuery));
            if (mode3d) {
                changeShowMode(mode3d === 'true' ? 1 : 2);
                dispatch(locationSelectorActions.changeModeShow3d(mode3d === 'true'));
            }
            if (isShowMeshNotComplete) {
               dispatch(locationSelectorActions.changeShowMeshNotComplete(isShowMeshNotComplete === 'true'))
            }
            if (modeShowFloor) {
                dispatch(locationSelectorActions.setModeShowFloor(modeShowFloor === 'true' ? 2 : 1));
            }
            if (floorActive) {
               dispatch(locationSelectorActions.changeFloorActive(Number(floorActive)));
            }
        }
        const res = await dispatch(locationSelectorActions.fetchLocationAll());
        if (res.meta?.requestStatus?.toLocaleLowerCase() === 'fulfilled' && res.payload.data.length > 0)
        {
            //終了分を非表示の保持描画
            const savedHiddenRaw = localStorage.getItem("isShowMeshNotComplete");
            const savedHidden = savedHiddenRaw !== null ? savedHiddenRaw === "true" : true;
            await handleLocationCompletionDisplay(savedHidden);
            //打設場所選択項目の保持描画
            const savedLocation = localStorage.getItem("locationSelect");
            const isExist = res.payload.data.some((location: any) => location.id === savedLocation);
            if (!isExist)
            {
                localStorage.removeItem("locationSelect");
            }
            let locationIdToUse = savedLocation && isExist ? savedLocation : locationSelectQuery ?? res.payload.data[0]?.id;
            await dispatch(locationSelectorActions.changeLocationSelect(locationIdToUse));
            localStorage.setItem('currentLocationId', locationIdToUse);
            //打設場所に合わせて、モデル表示変更処理
            await dispatch(locationSelectorActions.fetchLocationMesh({ locationId: locationIdToUse, igoreFilter: true, }));
            await dispatch(constructionActions.setStatusCallGetListMesh(false));

            let resPump;
            resPump = await dispatch(shippingInformationActions.fetchPumpList({ LocationId: locationIdToUse }));
            if (resPump?.meta?.requestStatus?.toLocaleLowerCase() === 'fulfilled')
            {
                const pumpList: IPump[] = resPump?.payload || [];
                let pumpSelectItem = pumpList[0].id;
                if (pumpSelectQuery && !isFirstLoadParam)
                {
                    dispatch(locationSelectorActions.changePumpSelect(pumpSelectQuery));
                    pumpSelectItem = pumpSelectQuery;
                } else
                {
                    dispatch(locationSelectorActions.changePumpSelect(pumpList[0].id));
                }
                // get pump is selected with information mode slim
                const pumpItem = pumpList.find(item => item.id === pumpSelectItem);
                if (pumpItem)
                {
                    dispatch(locationSelectorActions.setModeSlim(pumpItem.isSlim));
                }
            }
        }
        setFirstLoadFarm(true);
        return true;
    }, [])

    useEffect(() => {
        if (locationSelectorState.locationListSuccess?.length === 0)
        {
            getMeshFirst()
        }
    }, []);

    const getInfoMeshSetting = async () => {
        try {
            const dataMeshsetting = await api.meshSettingAPI.fetch();
            if (dataMeshsetting.data.data)
            {
                setDataMeshSetting({ data: dataMeshsetting.data.data });
            }
        }
        catch (error)
        {
            console.log('getInfoMeshSetting: ', error)
        }
    }

    useEffect(() => {
        const getSettingLocal = localStorage.getItem('meshSetting');
        if (getSettingLocal) {
            const dataMeshSettingLocal = JSON.parse(getSettingLocal);
            setDataMeshSetting({ data: dataMeshSettingLocal });
            dispatch(meshSettingActions.setMeshTransferTimeAll(dataMeshSettingLocal));
        } else {
            getInfoMeshSetting();
        }
    }, [])

    const revertByPump = async () => {
        try {
            if (locationSelectorState.pumpSelect) {
                const response = await dispatch(constructionActions.revertByPump(locationSelectorState.pumpSelect));
                if (response.payload && response.payload?.success) {
                    success('すべての打設中メッシュを一括で取り消しました。');
                    setShowModalDelete(false);
                    dispatch(locationSelectorActions.fetchLocationMesh({ locationId: locationSelectorState.locationSelect, igoreFilter: true }));
                } else {
                    error(response?.payload?.errors[0]);
                }
            }
        } catch (err) {
            error('システムに問題が発生しました。');
        }
    }

    const callApiStartStatus = async () => {
        try {
            const dataSend = {
                id: locationSelectorState.locationSelect,
                status: STATUS_MESH[1]
            }
            const response = await dispatch(constructionActions.fetchLocationUpdateStatus(dataSend));
            if (response.payload && response.payload?.success) {
                success('作業中に変更しました。');
                setShowModalStartStatus(false);
                dispatch(locationSelectorActions.fetchLocationMesh({ locationId: locationSelectorState.locationSelect, igoreFilter: true }));
                await dispatch(locationSelectorActions.fetchLocationAll());
                await dispatch(
                    overlappingActions.fetchList({
                        LocationId: locationSelectorState.locationSelect,
                    })
                );
                await dispatch(locationSelectorActions.changeShowMeshNotComplete(locationSelectorState.filter.isShowMeshNotComplete))
            } else {
                error(response?.payload?.errors[0]);
            }
        } catch (err) {
            error('システムに問題が発生しました。');
        }
    }

    const callApiSuccessStatus = async () => {
        try {
            const currentId = locationSelectorState.locationSelect;
            const dataSend = {
                id: currentId,
                status: STATUS_MESH[2]
            }
            const response = await dispatch(constructionActions.fetchLocationUpdateStatus(dataSend));
            if (!response.payload?.success) {
                error(response?.payload?.errors[0]);
                return;
            }
            success('作業終了に変更しました。');
            setShowModalSuccessStatus(false);
            const listRes = await dispatch(locationSelectorActions.fetchLocationAll());
            let nextLocationId = currentId;
            if (listRes.payload?.data?.length > 0) {
                const filteredList = locationSelectorState.filter.isShowMeshNotComplete
                    ? listRes.payload.data.filter(
                        (l: any) => l.status !== STATUS_MESH[2]
                    )
                    : listRes.payload.data;
                const exists = filteredList.some(
                    (l: any) => l.id === currentId
                );
                if (!exists && filteredList.length > 0) {
                    nextLocationId = filteredList[0].id;
                    await handleChangeLocation(
                        nextLocationId,
                        locationSelectorState.filter.isShowMeshNotComplete
                    );
                    localStorage.setItem("locationSelect", nextLocationId);
                    localStorage.setItem('currentLocationId', nextLocationId);
                }
            }
            await dispatch(overlappingActions.fetchList({ LocationId: nextLocationId, }));
            await dispatch(locationSelectorActions.changeShowMeshNotComplete(locationSelectorState.filter.isShowMeshNotComplete));
        } catch (err) {
            error('システムに問題が発生しました。');
        }
    };

    // #8424 Q-002: per-cell 打設開始/終了/取消 via Mesh/SiteUpdate for the tapped 2D cell.
    // status = MeshStatus code ('BeforePumping'|'DuringPumping'|'FinishPumping'); isCancel
    // distinguishes 開始取消 / 終了取消 from a normal forward transition.
    const handleSiteUpdate2D = async (status: string, isCancel: boolean) => {
        if (!meshActive2D?.meshId) { setModal2DStatus(null); return }
        // SKY-171: starting another ポンプ車's cell pours it with the SELECTED ポンプ車, as 3D
        // does (ThreeEditor sends pumpSelect): the BE re-assigns the cell to that pump and ends
        // that pump's previous 打設中 cell. Sending the cell's own pump kept it on the other pump
        // and ended THAT pump's 打設中 cell instead.
        const pumpId = status === STATUS_MESH[1] && !isCancel && meshActive2D.otherPump
            ? locationSelectorState.pumpSelect
            : meshActive2D.pumpId
        try {
            const response: any = await dispatch(constructionActions.fetchUpdateMesh({
                id: meshActive2D.meshId,
                status,
                isCancel,
                pumpId
            }))
            if (response.payload && response.payload?.success) {
                // SKY-137: メッシュクリック時の「メッシュの状態を更新しました。」は表示しない。
                // 状態変更のフィードバックはソケット通知「○-○ が○○に変わりました。」で行われる。
                setModal2DStatus(null)
                // SKY-193: paint the tapped cell NOW from the SiteUpdate response (the same fields the
                // socket handler feeds updateStatusMesh: id, status, warningStatus, pumpId, isSlim),
                // then reload the whole mesh behind it without the full-screen spinner. Before, the
                // user sat behind the spinner for a second round trip (GetWithMesh, 0.3-0.8 s on the
                // test host, more on a tablet) before the cell changed colour. Cells the BE changed
                // alongside (the pump's previous 打設中 cell) arrive with that reload / the socket.
                if (response.payload.data?.id) {
                    dispatch(locationSelectorActions.updateStatusMesh(response.payload.data))
                }
                dispatch(locationSelectorActions.fetchLocationMesh({ locationId: locationSelectorState.locationSelect, igoreFilter: true, silent: true }))
                // keep the 打重ね情報 table (overlapping list) in sync with the overlay
                dispatch(overlappingActions.fetchList({ LocationId: locationSelectorState.locationSelect }))
            } else {
                error(response?.payload?.errors?.[0] || 'システムに問題が発生しました。')
            }
        } catch (err) {
            error('システムに問題が発生しました。')
        }
    }

    const renderMenuDoing = () => {
        const locationStatus = locationSelectorState.locationList.data.find(location => location.id === locationSelectorState.locationSelect);
        if (locationStatus?.status === STATUS_MESH[1] || locationStatus?.status === STATUS_MESH[0]) {
            return <li className="disabled"><span>作業中</span></li>
        } else {
            return <li><a aria-hidden="true" onClick={() => {
                setShowModalStartStatus(true)
                if (document.getElementById("toggler-construction") !== undefined) {
                    /* @ts-ignore */
                    document.getElementById("toggler-construction").checked = false;
                }
            }}>作業中</a></li>
        }
    }

    const renderMenuSuccess = () => {

        const locationStatus = locationSelectorState.locationList.data.find(location => location.id === locationSelectorState.locationSelect);

        if (locationStatus?.status === STATUS_MESH[2]) {
            return <li className="disabled"><span>作業終了</span></li>
        } else {
            return <li><a aria-hidden="true" onClick={() => {
                setShowModalSuccessStatus(true);
                if (document.getElementById("toggler-construction") !== undefined) {
                    /* @ts-ignore */
                    document.getElementById("toggler-construction").checked = false;
                }
            }}>作業終了</a></li>
        }
    }

    const handleChangeLocation = async (locationId: string, checked?: boolean)  => {
        dispatch(locationSelectorActions.changeLocationSelect(locationId));
        dispatch(constructionActions.setStatusCallGetListMesh(false))
        dispatch(locationSelectorActions.changeFloorActive(-1))
        dispatch(locationSelectorActions.actionConstructionUpdate({
            ...locationSelectorState.filter,
            floorSelect: -1,
            showCompleted: false,
            istStatus: [],
            isShowMeshNotComplete: checked,
        }))
        const resPumpList = await dispatch(shippingInformationActions.fetchPumpList({ LocationId: locationId }));
        // SKY-162: select the first pump of the list just fetched for the NEW location.
        // Reading shippingInformationState.pumpList here takes the value captured when this
        // handler was created, i.e. the PREVIOUS location's pumps. Every location owns its own
        // ポンプ車1 (a distinct id with the same display name), so pumpSelect kept an id that
        // belongs to another location and every mesh of the new one was painted as 別ポンプ (gray).
        const nextPumpList: IPump[] = resPumpList.payload || [];
        if (nextPumpList[0]) {
            dispatch(locationSelectorActions.changePumpSelect(nextPumpList[0].id));
        }
        locationSelectorState.locationListSuccess.find(location => location.id === locationId);
        if (resPumpList.payload?.length > 0) {
            dispatch(locationSelectorActions.setModeSlim(resPumpList.payload[0].isSlim))
        }
        // SKY-163: reload the mesh of the new 打設場所. In 2D the page renders <Overlay2D>
        // instead of <Mode2dEditor>, so the refetch that component performs when
        // locationSelect changes (Mode2dEditor.tsx) never runs; nothing else refreshes
        // locationMesh either (the SignalR handler only dispatches fetchLocationMeshAlert,
        // which updates isShowAlert alone). The grid and the 打重ね情報 list therefore kept
        // the previous location's statuses until the page was reloaded.
        // Dispatched after changePumpSelect so the fresh mesh is painted with the pump of
        // the location it actually belongs to.
        dispatch(locationSelectorActions.fetchLocationMesh({ locationId, igoreFilter: true }));
        localStorage.setItem('locationSelect', locationId);
        localStorage.setItem('currentLocationId', locationId);
    }

    const handlerSave = async (values: any) => {
        try {
            localStorage.setItem('meshSetting', JSON.stringify(values.data));
            dispatch(meshSettingActions.setMeshTransferTimeAll(values.data));
            setDataMeshSetting(values);
            setShowModal(false);
        } catch (error) {
            console.log('handlerSave: ', error)
        }
    }

    const isHideAll = locationSelectorState.filter.isShowMeshNotComplete && locationSelectorState.locationListSuccess.length === 0;
    type LocationType =
        RootState["locationSelector"]["locationList"]["data"][number];
    const handleLocationCompletionDisplay = async (
        checked: boolean,
        sourceList?: LocationType[]
    ) => {
        localStorage.setItem("isShowMeshNotComplete", String(checked));
        dispatch(locationSelectorActions.changeShowMeshNotComplete(checked));
        const baseList = sourceList ?? locationSelectorState.locationList.data;
        const filteredList = checked
            ? baseList.filter(
                location => location.status !== STATUS_MESH[2]
            )
            : baseList;
        const isCurrentVisible = filteredList.some(
            location => location.id === locationSelectorState.locationSelect
        );
        if (!isCurrentVisible && filteredList.length > 0) {
            await handleChangeLocation(filteredList[0].id, checked);
        }
    };

    return (
        <>
            <LoadingSpinner isShowing={constructionState.isLoading || locationSelectorState.isLoading} />
            {!constructionState.isShowFullScreen && (
                <div className="header-construction">
                    <div className="title-header-construction text-center">
                        <img src="/assets/logo.png" alt="logo" className="ml-2" height="35" />
                    </div>
                    <div className="menu-construction-content">
                        <input type="checkbox" className="toggler-construction" id="toggler-construction" />
                        <div className={locationSelectorState.isModeSlim && locationSelectorState.isShowAlert ? 'hamburger-construction construction-alert' : 'hamburger-construction'} id="hamburger-construction"><div></div></div>
                        <div className="menu-construction">
                            <div>
                                <ul>
                                    <li><a aria-hidden="true" onClick={() => {
                                        setShow(true)
                                        if (document.getElementById("toggler-construction") !== undefined) {
                                            /* @ts-ignore */
                                            document.getElementById("toggler-construction").checked = false;
                                        }
                                    }}>メッシュ色凡例</a></li>
                                    {/* #8432: hide メッシュ透過率設定 in 2D mode (only used for 3D model) */}
                                    {!is2D && (
                                    <li><a aria-hidden="true" onClick={() => {
                                        const getSettingLocal = localStorage.getItem('meshSetting');
                                        if (getSettingLocal) {
                                            const dataMeshSettingLocal = JSON.parse(getSettingLocal);
                                            setDataMeshSetting({ data: dataMeshSettingLocal })
                                        }
                                        setShowModal(true);
                                        if (document.getElementById("toggler-construction") !== undefined) {
                                            /* @ts-ignore */
                                            document.getElementById("toggler-construction").checked = false;
                                        }
                                    }}>メッシュ透過率設定</a></li>
                                    )}
                                    <li>作業状態
                                        <ul>
                                            {isHideAll ? (
                                                <>
                                                    <li className="disabled"><span>作業中</span></li>
                                                    <li className="disabled"><span>作業終了</span></li>
                                                </>
                                            ) : (
                                                <>
                                                    {renderMenuDoing()}
                                                    {renderMenuSuccess()}
                                                </>
                                            )}
                                        </ul>
                                    </li>
                                    <li>
                                        <a
                                            aria-hidden="true"
                                            className={isHideAll ? "disabled" : undefined}
                                            style={isHideAll ? { pointerEvents: "none", color: "#a7a6a6", textDecoration: "none" } : undefined}
                                            onClick={() => {
                                                if (isHideAll) return;

                                                setShowModalDelete(true)
                                                if (document.getElementById("toggler-construction") !== undefined) {
                                                    /* @ts-ignore */
                                                    document.getElementById("toggler-construction").checked = false;
                                                }
                                            }}
                                        >
                                            開始取消
                                        </a>
                                    </li>
                                    {/* #8432: hide the "2D表示へ切替" button in 2D mode (mode is fixed system-wide) */}
                                    {!is2D && (
                                    <li>
                                        <a
                                            aria-hidden="true"
                                            className={isHideAll ? "disabled" : undefined}
                                            style={isHideAll ? { pointerEvents: "none", color: "#a7a6a6", textDecoration: "none" } : undefined}
                                            onClick={() => {
                                                if (isHideAll) return;

                                                if (showMode === 1) {
                                                    changeShowMode(2);
                                                    dispatch(locationSelectorActions.changeModeShow3d(false));
                                                    dispatch(locationSelectorActions.changeFloorActive(1));
                                                    dispatch(locationSelectorActions.actionConstructionUpdate({
                                                        ...locationSelectorState.filter,
                                                        floorSelect: 1,
                                                    }));
                                                } else {
                                                    changeShowMode(1);
                                                    dispatch(locationSelectorActions.changeModeShow3d(true));
                                                    dispatch(locationSelectorActions.changeFloorActive(-1));
                                                    dispatch(locationSelectorActions.actionConstructionUpdate({
                                                        ...locationSelectorState.filter,
                                                        floorSelect: -1,
                                                    }));
                                                }

                                                if (document.getElementById("toggler-construction") !== undefined) {
                                                    /* @ts-ignore */
                                                    document.getElementById("toggler-construction").checked = false;
                                                }
                                            }}
                                        >
                                            {showMode === 1 ? '2D' : '3D'}表示へ切替
                                        </a>
                                    </li>
                                    )}
                                    <li><a aria-hidden="true" onClick={() => dispatch(authActions.logoutWorkingSite())}>ログアウト</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {!constructionState.isShowFullScreen && (
                <div className="section hiddenBlockSuccess-content">
                    <div className="col-12 d-flex">
                        <div className="content-form-check d-flex align-items-center">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={locationSelectorState.filter.isShowMeshNotComplete}
                                    id="hiddenBlockSuccess"
                                    onChange={() => handleLocationCompletionDisplay(!locationSelectorState.filter.isShowMeshNotComplete)} />
                                <label className="form-check-label text-show-mode text-white" htmlFor="hiddenBlockSuccess">終了分を非表示</label>
                            </div>
                        </div>
                        {/* #8431: hide 層の表示方法 (layer display method) in 2D mode */}
                        {showMode === 1 && !is2D && (
                            <div className="switch-show-mode">
                                <div className="d-flex align-items-center justify-content-center">
                                    <div className="text-white text-show-mode">層の表示方法</div>
                                    <div className="ml-5">
                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="options-outlined"
                                            id="full-layer"
                                            autoComplete="off"
                                            checked={locationSelectorState.modeShowFloor === 2}
                                            onChange={() => {
                                                dispatch(locationSelectorActions.setModeShowFloor(2));
                                                if (locationSelectorState.floorActive > -1) {
                                                    dispatch(locationSelectorActions.actionConstructionUpdate(locationSelectorState.filter));
                                                }
                                            }}
                                        />
                                        <label className={`btn btn-show-mode btn-sm ${locationSelectorState.modeShowFloor === 2 ? 'btn btn-outline-warning' : 'btn-light'}`} htmlFor="full-layer">全層</label>
                                        <input
                                            type="radio"
                                            className="btn-check"
                                            name="options-outlined"
                                            id="washing-tub"
                                            autoComplete="off"
                                            checked={locationSelectorState.modeShowFloor === 1}
                                            onChange={() => {
                                                dispatch(locationSelectorActions.setModeShowFloor(1));
                                                if (locationSelectorState.floorActive > -1) {
                                                    dispatch(locationSelectorActions.actionConstructionUpdate(locationSelectorState.filter));
                                                }
                                            }}
                                        />
                                        <label className={`btn btn-show-mode ml-5 btn-sm ${locationSelectorState.modeShowFloor === 1 ? 'btn btn-outline-warning' : 'btn-light'}`} htmlFor="washing-tub">選択層のみ</label>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                </div>
            )}
            {!constructionState.isShowFullScreen && (
                <div className="section list-select-construction">
                    <div className="d-flex">
                        <div className="col-7">
                            <select
                                className="form-select"
                                value={locationSelectorState.locationSelect}
                                onChange={event => {
                                    const value = event.target.value;
                                    localStorage.setItem("locationSelect", value);
                                    handleChangeLocation(
                                        value,
                                        locationSelectorState.filter.isShowMeshNotComplete
                                    );
                                }}
                            >
                                {locationSelectorState.locationListSuccess?.map(itemLocation => (
                                    <option key={itemLocation.id} value={itemLocation.id}>
                                    {itemLocation.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-5">
                            <select
                                className="form-select"
                                value={locationSelectorState.pumpSelect}
                                onChange={async (event: any) => {
                                    const value = event.target.value;
                                    await dispatch(locationSelectorActions.changePumpSelect(value));
                                    const pumpItem = shippingInformationState.pumpList.find(item => item.id === value);
                                    if (pumpItem)
                                    {
                                        dispatch(locationSelectorActions.setModeSlim(pumpItem.isSlim));
                                    }
                                }}
                            >
                                {shippingInformationState.pumpList?.map(itemPump => (
                                    <option key={itemPump.id} value={itemPump.id}>
                                        {itemPump.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
            )}

            <div className="section position-relative">
                {locationSelectorState.isShowAlert && !locationSelectorState.isModeSlim && (<div className={`alert-message-slim text-white position-absolute ${showMode !== 1 ? 'alert-message-slim-2d' : ''}`}>薄層打設済みのメッシュがあります。</div>)}
                {/* #8431: in system-2D show the PNG overlay (interactive site view) instead of the 3D/GLB editors */}
                {is2D ? (
                    <div className="position-relative"
                        // SKY-124: on zoom, grow the 2D area to fill the space freed by the hidden
                        //   header/list so Overlay2D (flex:1) stretches vertically and the mesh scales up.
                        style={constructionState.isShowFullScreen ? { height: 'calc(100vh - 56px)', display: 'flex', flexDirection: 'column' } : undefined}
                        title={meshActive2D ? `選択セル: ${meshActive2D.layer}-${meshActive2D.row}` : undefined}>
                    {/* 現場_詳細②_5 ㉗㉘㉙: 拡大/縮小 for the two-image area (same action as the 3D editor) */}
                    <span
                        style={{ position: 'absolute', top: 10, right: 14, zIndex: 10, fontSize: 20, cursor: 'pointer' }}
                        aria-hidden="true"
                    >
                        {constructionState.isShowFullScreen ? (
                            <i className="fa fa-compress" aria-hidden="true" onClick={() => dispatch(constructionActions.setShowFullScreen(false))}></i>
                        ) : (
                            <i className="fa fa-expand" aria-hidden="true" onClick={() => dispatch(constructionActions.setShowFullScreen(true))}></i>
                        )}
                    </span>
                    <Overlay2D
                        locationId={locationSelectorState.locationSelect}
                        fullScreen={constructionState.isShowFullScreen}
                        meshList={(locationSelectorState.locationMesh || []).reduce((acc: any[], loc: any) => acc.concat(loc.meshes || []), []).map((m: any) => ({
                            id: m.id,
                            layer: m.layer,
                            row: m.row,
                            // status is a STATUS_MESH code string ('BeforePumping'…) — normalise to index
                            status: typeof m.status === 'number' ? m.status : STATUS_MESH.indexOf(m.status),
                            warningStatus: m.warningStatus, // BE code string ('WarningTimeExceeded'…)
                            pumpId: m.pumpId,
                            // SKY-138: ポンプ車選択中は、選択外ポンプの未打設メッシュを灰色表示（eagle3D同様）。
                            // SKY-162: a cell with no ポンプ車 assigned (PumpId NULL — the PNG's G
                            // channel was 0, see MeshCreateBatch2DRequest.ResolvePumpId) belongs to
                            // no pump at all, so it must not be greyed as "another pump's cell".
                            pumpMismatch: !!locationSelectorState.pumpSelect
                                && !!m.pumpId
                                && m.pumpId !== locationSelectorState.pumpSelect,
                            // SKY-172: as in 3D, another pump's 打設中 / 打設済 cell is gray too
                            grayAnyStatus: true,
                        }))}
                        onCellClick={(cell) => {
                            // #8424 Q-002: open the modal matching the tapped cell's CURRENT status.
                            const meshes = (locationSelectorState.locationMesh || []).reduce((acc: any[], loc: any) => acc.concat(loc.meshes || []), [])
                            const mesh = meshes.find((m: any) => (cell.meshId && m.id === cell.meshId) || (m.layer === cell.layer && m.row === cell.row))
                            const idx = mesh ? STATUS_MESH.indexOf(mesh.status) : 0
                            // Same condition as the gray pumpMismatch colour above, so a cell with no
                            // ポンプ車 (SKY-162) is never treated as another pump's cell.
                            const isOtherPump = !!locationSelectorState.pumpSelect
                                && !!mesh?.pumpId && mesh.pumpId !== locationSelectorState.pumpSelect
                            // SKY-172: another pump's 打設中 / 打設済 cell is view-only for the selected
                            // pump — refuse it with 3D's messages (ThreeEditor) instead of opening the
                            // 打設終了 / 取消 modal, which let e.g. ポンプ車2 change ポンプ車1's times.
                            if (isOtherPump && idx === 1) {
                                error('打設開始済みです。')
                                return
                            }
                            if (isOtherPump && idx === 2) {
                                error('打設済みです。')
                                return
                            }
                            // 現場_詳細②_4 ⑲/㉖: a 未打設 cell whose under cell is still 未打設 cannot
                            // start — show the error immediately instead of opening the modal.
                            // SKY-165: decide from the UnderMesh relations returned by the BE
                            // (underMeshIds) and apply the same rule as MeshSiteUpdateRequest: blocked
                            // only when the cell HAS cells beneath it and none of them (non-薄層) has
                            // started. The previous code guessed the cell below from pixel distances
                            // at tap time, accepting any lower cell within half of the tapped cell's
                            // larger side (~211px on the customer's staircase PNG), so the new edge
                            // cell of an upper layer was matched to a far-away cell and blocked even
                            // though nothing lies beneath it. A cell with no relation (lowest layer,
                            // staircase edge) is never blocked here; if the BE does not send
                            // underMeshIds the BE check in Mesh/SiteUpdate remains the authority.
                            if (idx <= 0 && mesh) {
                                const underIds: string[] = mesh.underMeshIds || []
                                const unders = meshes.filter((m: any) => underIds.includes(m.id))
                                const anyStarted = unders.some((u: any) => !u.isSlim
                                    && (u.status === STATUS_MESH[1] || u.status === STATUS_MESH[2]))
                                if (unders.length > 0 && !anyStarted) {
                                    error('下の層が未打設です。')
                                    return
                                }
                            }
                            // SKY-171: another pump's 未打設 cell may still be started, after a warning
                            const otherPump = idx <= 0 && isOtherPump
                            setMeshActive2D({ ...cell, meshId: mesh?.id ?? cell.meshId, pumpId: mesh?.pumpId, code: mesh?.code, otherPump })
                            setModal2DStatus(idx >= 0 ? idx : 0)
                        }}
                    />
                    </div>
                ) : (!isHideAll && (
                    showMode === 1 ? <ThreeEditor /> : <Mode2dEditor />
                ))}
            </div>
            {!constructionState.isShowFullScreen && !isHideAll && (
                <div className="section pb-2 list-view-info-location">
                    <span className="icon-change-mode-view">
                        <i className="fas fa-retweet" aria-hidden="true"
                            onClick={() =>
                                dispatch(constructionActions.setShowTableMesh(!constructionState.isShowTableMesh))
                            }
                        />
                    </span>
                    {!constructionState.isShowTableMesh
                        ? <ListMesh />
                        : <ShippingInformationList />
                    }
                </div>
            )}
            <Modal size="sm" show={show} onHide={() => setShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title className="font-14">メッシュ色凡例</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <table className="table table-bordered table-sm">
                        <thead>
                            <tr>
                                <th>色</th>
                                <th>ステータス</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>白</td>
                                <td>未打設</td>
                            </tr>
                            <tr className="bg-during-casting">
                                <td>黄</td>
                                <td>打設中</td>
                            </tr>
                            <tr className="bg-end-casting">
                                <td>黄緑</td>
                                <td>打設終了</td>
                            </tr>
                            <tr className="bg-warning-time-exceeded">
                                <td>ピンク</td>
                                <td>警告時間超過</td>
                            </tr>
                            <tr className="bg-time-limit-exceeded">
                                <td>赤</td>
                                <td>制限時間超過</td>
                            </tr>
                        </tbody>
                    </table>
                </Modal.Body>
            </Modal>
            <Modal show={showModalDelete}
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Body className="text-center">
                    <div className="content-confirm">
                        <div className="">打設中のすべてのメッシュを一括で取り消します。よろしいですか。</div>
                        <div className="d-flex justify-content-evenly pt-2">
                            <button className="btn btn-mesh-action btn-primary mr-2" onClick={revertByPump}>OK</button>
                            <button className="btn btn-mesh-action btn-secondary" onClick={() => setShowModalDelete(false)}>キャンセル</button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            <Modal show={showModalStartStatus}
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Body className="text-center">
                    <div className="content-confirm">
                        <div className="">打設場所を作業中に変更します。よろしいですか。</div>
                        <div className="d-flex justify-content-evenly pt-2">
                            <button className="btn btn-mesh-action btn-primary mr-2" onClick={callApiStartStatus}>OK</button>
                            <button className="btn btn-mesh-action btn-secondary" onClick={() => setShowModalStartStatus(false)}>キャンセル</button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            <Modal show={showModalSuccessStatus}
                aria-labelledby="contained-modal-title-vcenter"
                centered
            >
                <Modal.Body className="text-center">
                    <div className="content-confirm">
                        <div className="">打設場所を作業終了に変更します。よろしいですか。</div>
                        <div className="d-flex justify-content-evenly pt-2">
                            <button className="btn btn-mesh-action btn-primary mr-2" onClick={callApiSuccessStatus}>OK</button>
                            <button className="btn btn-mesh-action btn-secondary" onClick={() => setShowModalSuccessStatus(false)}>キャンセル</button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
            {/* #8424 Q-002: per-cell 2D status modal — content depends on the tapped cell's status.
                Label per mockup 現場_詳細②: 「メッシュ：<code>」 using the BE mesh code. */}
            {/* SKY-133: 打設開始/終了モーダルが 2D打重ね画像 と重ならないよう、中央ではなく
                下部（打重ね情報側）に表示する（dialogClassName で位置調整）。 */}
            <Modal show={modal2DStatus !== null} dialogClassName="modal-2d-lower" onHide={() => setModal2DStatus(null)}>
                <Modal.Body className="text-center">
                    <div className="content-confirm">
                        {modal2DStatus === 0 && (
                            <>
                                {meshActive2D?.otherPump ? (
                                    // SKY-171: tell the user the cell belongs to another ポンプ車 (3D wording)
                                    <>
                                        <div>別のポンプ車に割り当てられています。</div>
                                        <div>{`メッシュ：${meshActive2D?.code ?? `${meshActive2D?.layer}-${meshActive2D?.row}`}打設を開始しますか？`}</div>
                                    </>
                                ) : (
                                    <div>{`メッシュ：${meshActive2D?.code ?? `${meshActive2D?.layer}-${meshActive2D?.row}`} を打設開始します。よろしいですか。`}</div>
                                )}
                                <div className="d-flex justify-content-evenly pt-2">
                                    <button className="btn btn-mesh-action btn-primary mr-2" onClick={() => handleSiteUpdate2D('DuringPumping', false)}>打設開始</button>
                                    <button className="btn btn-mesh-action btn-secondary" onClick={() => setModal2DStatus(null)}>キャンセル</button>
                                </div>
                            </>
                        )}
                        {modal2DStatus === 1 && (
                            <>
                                <div>{`メッシュ：${meshActive2D?.code ?? `${meshActive2D?.layer}-${meshActive2D?.row}`} の操作を選択してください。`}</div>
                                <div className="d-flex justify-content-evenly pt-2">
                                    <button className="btn btn-mesh-action btn-primary mr-2" onClick={() => handleSiteUpdate2D('FinishPumping', false)}>打設終了</button>
                                    {/* SKY-135: 打設開始取消 は赤色 */}
                                    <button className="btn btn-mesh-action btn-danger mr-2" onClick={() => handleSiteUpdate2D('BeforePumping', true)}>打設開始取消</button>
                                    <button className="btn btn-mesh-action btn-secondary" onClick={() => setModal2DStatus(null)}>キャンセル</button>
                                </div>
                            </>
                        )}
                        {modal2DStatus === 2 && (
                            <>
                                <div>{`メッシュ：${meshActive2D?.code ?? `${meshActive2D?.layer}-${meshActive2D?.row}`} の打設終了を取り消します。よろしいですか。`}</div>
                                <div className="d-flex justify-content-evenly pt-2">
                                    {/* SKY-135: 終了取消 は赤色 */}
                                    <button className="btn btn-mesh-action btn-danger mr-2" onClick={() => handleSiteUpdate2D('DuringPumping', true)}>終了取消</button>
                                    <button className="btn btn-mesh-action btn-secondary" onClick={() => setModal2DStatus(null)}>キャンセル</button>
                                </div>
                            </>
                        )}
                    </div>
                </Modal.Body>
            </Modal>
            <Modal show={isShowModal}
                aria-labelledby="contained-modal-title-vcenter"
                centered
                backdrop="static"
            >
                <Modal.Header>メッシュ透過率設定</Modal.Header>
                <Modal.Body className="text-center">
                    <Formik
                        enableReinitialize
                        initialValues={dataMeshSetting}
                        validationSchema={meshSettingValidationSchema}
                        onSubmit={(values, actions) => {
                            handlerSave(values);
                            actions.setSubmitting(false)
                        }}
                    >
                        {({ values, handleReset }) => (
                            <Form className="form-update">
                                <div className="checkbox mb-3 d-flex justify-content-end">
                                    <button type="button" className="btn btn-primary" onClick={() => {
                                        handleReset();
                                        getInfoMeshSetting();
                                    }}>リセット</button>
                                </div>
                                <div>
                                    <table className="table table-bordered">
                                        <tbody>
                                            {values.data?.map((value: any, index: number) => (
                                                <tr key={value.id}>
                                                    <td>{value.name}</td>
                                                    <td>{value.colorName}</td>
                                                    <td>
                                                        <Field type="number" name={`data.${index}.transferTime`} className="form-control w-100" />
                                                        <ErrorMessage name={`data.${index}.transferTime`}>
                                                            {msg => <div className="text-danger">{msg}</div>}
                                                        </ErrorMessage>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div className="mt-4 text-center d-flex justify-content-evenly">
                                    <Button className="w-120" variant="primary" type="submit">登録</Button>
                                    <Button className="w-120" variant="secondary" onClick={() => {
                                        setShowModal(false);
                                    }}>
                                        キャンセル
                                    </Button>
                                    </div>
                            </Form>
                        )}
                    </Formik>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default connect((state: RootState) => ({
    locationSelectorState: state.locationSelector,
    shippingInformationState: state.shippingInformation,
    constructionState: state.construction,
    meshSettingState: state.meshSetting,
}))(ConstructionPage);
